import { useTranslation } from 'react-i18next';
import { FolderOpen } from 'lucide-react';

import Sprocket from '@/components/ui/Sprocket';
import type { ProductionStep } from '@/hooks/useProductionSteps';
import { cn } from '@/lib/utils';

function RailStep({
  step,
  index,
  active,
  onSelect,
}: {
  step: ProductionStep;
  index: number;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'step' : undefined}
      className={cn(
        'relative flex w-full items-start gap-2.5 rounded-lg py-2.5 pl-3 pr-2 text-left transition-colors',
        active ? 'bg-selected' : 'hover:bg-foreground/[0.04]',
      )}
    >
      {active && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />}
      <Sprocket index={index} state={active ? 'active' : step.state === 'done' ? 'done' : 'pending'} />
      <span className="min-w-0 pt-0.5">
        <span
          className={cn(
            'block truncate text-[12.5px] font-medium',
            active || step.state === 'done' ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {step.label}
        </span>
        <span className="mt-0.5 block truncate text-[10px] text-faint">{step.status}</span>
      </span>
    </button>
  );
}

/**
 * The production path — the film's journey from script to delivery, as a numbered
 * rail. This replaced a flat tab strip: a tab could only be present or greyed out,
 * whereas each rail step reports where it actually is ("In progress · 1/6 frames"),
 * which is what makes the pipeline legible to someone new to film production.
 */
export default function ProductionRail({
  steps,
  activeKey,
  onSelect,
}: {
  steps: ProductionStep[];
  activeKey: string;
  onSelect: (key: string) => void;
}) {
  const { t } = useTranslation('production');
  const pipeline = steps.filter((s) => !s.utility);
  const utility = steps.filter((s) => s.utility);

  return (
    <nav aria-label={t('rail.title')}>
      <div className="mb-3 px-2 text-[10px] font-semibold uppercase tracking-wider text-faint">{t('rail.title')}</div>
      {pipeline.map((step, i) => (
        <RailStep key={step.key} step={step} index={i + 1} active={step.key === activeKey} onSelect={() => onSelect(step.key)} />
      ))}

      {utility.length > 0 && (
        <>
          <div className="my-3.5 border-t border-dashed border-border" />
          {utility.map((step) => (
            <button
              key={step.key}
              type="button"
              onClick={() => onSelect(step.key)}
              className={cn(
                'relative flex w-full items-center gap-2.5 rounded-lg py-2.5 pl-3 pr-2 text-left transition-colors',
                step.key === activeKey ? 'bg-selected' : 'hover:bg-foreground/[0.04]',
              )}
            >
              {step.key === activeKey && (
                <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />
              )}
              <FolderOpen className="h-[18px] w-[18px] shrink-0 text-primary" />
              <span
                className={cn(
                  'truncate text-[12.5px] font-medium',
                  step.key === activeKey ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
            </button>
          ))}
        </>
      )}
    </nav>
  );
}
